/**
 * Datos para estudio (4/11/2026).
 *
 * «Que luego permita exportar datos como refracción residual con cada lente
 * en cada paciente, o estudios así… una sección en el menú de datos para
 * estudio.»
 *
 * Toda la historia oftalmológica de la clínica en tablas (una fila por ojo y
 * acto), con los resultados que se publican —error de predicción de cada
 * LIO, refracción residual, eficacia y seguridad de la refractiva— y su
 * exportación a Excel o CSV. Seudonimizado por defecto: código de estudio en
 * vez de nombre. Lo calcula el servidor (services/oftalmo_estudios.py).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis,
} from 'recharts';
import { api } from '../lib/api';
import { PageHeader, MiniStat } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { FileSpreadsheet, Download, Loader2, FlaskConical } from 'lucide-react';
import { aCsv, dec, descargar } from '../lib/oftalmo';

const TABLAS = [
  ['lio', 'Cataratas · LIO'], ['refractiva', 'Refractiva'], ['pio', 'PIO'], ['oct', 'OCT'],
  ['inyecciones', 'Inyecciones'], ['campo_visual', 'Campo visual'], ['topografia', 'Topografía'], ['lesiones', 'Lesiones medidas'],
];

const COLUMNAS = {
  lio: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['ojo', 'Ojo'], ['fecha_cirugia', 'Cirugía'],
    ['cirujano', 'Cirujano'], ['modelo', 'Modelo LIO'], ['tipo_lente', 'Tipo'], ['potencia', 'Potencia'],
    ['formula', 'Fórmula'], ['al', 'AL'], ['k_media', 'K media'], ['acd', 'ACD'], ['refraccion_esperada', 'Esperada'],
    ['esf_post', 'Esf. res.'], ['cil_post', 'Cil. res.'], ['eje_post', 'Eje res.'], ['ee_post', 'EE residual'],
    ['error_prediccion', 'Error pred.'], ['av_sc_post', 'AVsc'], ['av_cc_post', 'AVcc'], ['dias_postop', 'Días'],
    ['incidencias', 'Incidencias']],
  refractiva: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['ojo', 'Ojo'], ['fecha_cirugia', 'Cirugía'],
    ['tecnica', 'Técnica'], ['esf_subj', 'Esf. subj.'], ['cil_subj', 'Cil. subj.'], ['ee_subj', 'EE subj.'],
    ['ee_ciclo', 'EE ciclo.'], ['objetivo_ee', 'Objetivo'], ['ablacion_um', 'Ablación'], ['lecho_um', 'Lecho'], ['pta', 'PTA %'],
    ['esf_residual', 'Esf. res.'], ['cil_residual', 'Cil. res.'], ['ee_residual', 'EE residual'],
    ['error_vs_objetivo', 'Error'], ['av_cc_pre', 'AVcc pre'], ['av_sc_post', 'AVsc post'], ['av_cc_post', 'AVcc post'],
    ['indice_eficacia', 'Eficacia'], ['indice_seguridad', 'Seguridad'], ['dias_postop', 'Días']],
  pio: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['fecha', 'Fecha'], ['ojo', 'Ojo'], ['pio', 'PIO'],
    ['paquimetria', 'Paquimetría'], ['metodo', 'Método'], ['tratamiento', 'Tratamiento']],
  oct: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['fecha', 'Fecha'], ['ojo', 'Ojo'], ['cst', 'Grosor central'],
    ['volumen', 'Volumen'], ['cfnr', 'CFNR'], ['inyecciones_previas', 'Inyecciones previas'], ['diagnostico', 'Diagnóstico']],
  inyecciones: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['fecha', 'Fecha'], ['ojo', 'Ojo'],
    ['farmaco', 'Fármaco'], ['indicacion', 'Indicación'], ['numero_en_ese_ojo', 'Nº'], ['lote', 'Lote']],
  campo_visual: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['fecha', 'Fecha'], ['ojo', 'Ojo'],
    ['estrategia', 'Estrategia'], ['md', 'MD'], ['psd', 'PSD'], ['vfi', 'VFI'], ['fiabilidad', 'Fiabilidad']],
  topografia: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['fecha', 'Fecha'], ['ojo', 'Ojo'], ['k1', 'K1'],
    ['k2', 'K2'], ['kmax', 'Kmax'], ['astig', 'Astig.'], ['paqui_min', 'Paqui. mín.'], ['diagnostico', 'Diagnóstico'], ['valoracion', 'Valoración']],
  lesiones: [['codigo', 'Código'], ['paciente', 'Paciente'], ['edad', 'Edad'], ['fecha', 'Fecha'], ['ojo', 'Ojo'], ['prueba', 'Prueba'],
    ['diagnostico', 'Diagnóstico'], ['lesion', 'Lesión'], ['area_mm2', 'Área (mm²)'], ['diametro_mm', 'Diámetro (mm)']],
};
// Por qué columna se puede filtrar cada tabla.
const FILTRO = { lio: ['modelo', 'Modelo de LIO'], refractiva: ['tecnica', 'Técnica'], inyecciones: ['farmaco', 'Fármaco'] };

function celda(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? v : String(Math.round(v * 100) / 100).replace('.', ',');
  return v;
}

function Histograma({ errores, titulo }) {
  const bins = [];
  for (let x = -1.5; x < 1.5; x += 0.25) bins.push({ desde: x, n: 0 });
  errores.forEach((e) => {
    const i = Math.min(bins.length - 1, Math.max(0, Math.floor((e + 1.5) / 0.25)));
    bins[i].n += 1;
  });
  const datos = bins.map((b) => ({ ...b, etiqueta: `${b.desde > 0 ? '+' : ''}${b.desde.toFixed(2)}`, dentro: Math.abs(b.desde + 0.125) <= 0.5 }));
  return (
    <div className="cp-card p-4">
      <div className="font-display font-semibold text-sm mb-2">{titulo}</div>
      <div style={{ height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={datos} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,140,135,0.25)" />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 10 }} interval={1} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [v, 'ojos']} labelFormatter={(l) => `${l} a ${(Number(l) + 0.25).toFixed(2)} D`} />
            <Bar dataKey="n" isAnimationActive={false}>
              {datos.map((d) => <Cell key={d.etiqueta} fill={d.dentro ? '#2E6E5E' : '#F59E0B'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-xs text-muted-foreground">Verde: dentro de ±0,50 D.</div>
    </div>
  );
}

export default function DatosEstudioPage() {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [seud, setSeud] = useState(true);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [tabla, setTabla] = useState('lio');
  const [filtro, setFiltro] = useState('');
  const [bajando, setBajando] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (desde) p.set('desde', desde);
    if (hasta) p.set('hasta', hasta);
    p.set('seudonimizar', seud ? 'true' : 'false');
    return p.toString();
  }, [desde, hasta, seud]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    api.get(`/api/oftalmologia/estudios?${params}`)
      .then((r) => { if (vivo) setDatos(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudieron cargar los datos'))
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [params]);
  useEffect(() => { setFiltro(''); }, [tabla]);

  const filas = useMemo(() => {
    const xs = datos?.tablas?.[tabla] || [];
    const f = FILTRO[tabla];
    return f && filtro ? xs.filter((r) => r[f[0]] === filtro) : xs;
  }, [datos, tabla, filtro]);
  const opcionesFiltro = useMemo(() => {
    const f = FILTRO[tabla];
    if (!f) return [];
    return [...new Set((datos?.tablas?.[tabla] || []).map((r) => r[f[0]]).filter(Boolean))].sort();
  }, [datos, tabla]);
  const cols = COLUMNAS[tabla].filter(([k]) => !seud || k !== 'paciente');

  const excel = async () => {
    setBajando(true);
    try {
      const r = await api.get(`/api/oftalmologia/estudios/excel?${params}`, { responseType: 'blob' });
      descargar(`datos-estudio-oftalmologia-${new Date().toISOString().slice(0, 10)}.xlsx`, r.data);
    } catch (e) {
      toast.error('No se pudo generar el Excel');
    } finally {
      setBajando(false);
    }
  };
  const csv = () => descargar(`${tabla}-${new Date().toISOString().slice(0, 10)}.csv`, aCsv(filas, cols));

  const res = datos?.resumen;
  const erroresLio = filas.filter(() => tabla === 'lio').map((r) => r.error_prediccion).filter((e) => e !== null && e !== undefined);
  const erroresRef = (tabla === 'refractiva' ? filas : []).map((r) => r.error_vs_objetivo).filter((e) => e !== null && e !== undefined);
  const resumenDe = (errs) => {
    if (!errs.length) return null;
    const abs = errs.map(Math.abs);
    return {
      n: errs.length, media: errs.reduce((a, b) => a + b, 0) / errs.length, mae: abs.reduce((a, b) => a + b, 0) / errs.length,
      p50: abs.filter((x) => x <= 0.5).length / errs.length * 100, p100: abs.filter((x) => x <= 1).length / errs.length * 100,
    };
  };
  const rl = resumenDe(erroresLio);
  const rr = resumenDe(erroresRef);
  const dispersion = (tabla === 'refractiva' ? filas : []).filter((r) => r.correccion_intentada !== null && r.correccion_conseguida !== null)
    .map((r) => ({ x: Math.abs(r.correccion_intentada), y: Math.abs(r.correccion_conseguida) }));

  return (
    <div>
      <PageHeader title="Datos para estudio" subtitle="Resultados de la historia oftalmológica de la clínica, listos para analizar o publicar."
        actions={<>
          <Button variant="outline" onClick={csv} disabled={!filas.length} data-testid="estudio-csv"><Download className="w-4 h-4 mr-1" />CSV de esta tabla</Button>
          <Button onClick={excel} disabled={bajando} data-testid="estudio-excel">{bajando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}Excel completo</Button>
        </>} />

      <div className="cp-card p-4 mb-4 flex flex-wrap items-end gap-4">
        <label className="text-xs text-muted-foreground flex flex-col gap-1">Desde<Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-40" /></label>
        <label className="text-xs text-muted-foreground flex flex-col gap-1">Hasta<Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-40" /></label>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={seud} onCheckedChange={setSeud} data-testid="estudio-seudonimizar" />
          Seudonimizado (código de estudio, sin nombre)
        </label>
        {cargando && <Loader2 className="w-4 h-4 animate-spin text-[#2E6E5E]" />}
      </div>

      {res && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="estudio-resumen">
          <MiniStat label="Ojos con LIO" value={res.lio.ojos} hint={`${res.lio.n || 0} con refracción postoperatoria`} icon={FlaskConical} />
          <MiniStat label="LIO dentro de ±0,50 D" value={res.lio.n ? `${dec(res.lio.pct_050, 0)} %` : '—'} hint={res.lio.n ? `error medio ${dec(res.lio.media, 2)} D · MAE ${dec(res.lio.mae, 2)} D` : ''} />
          <MiniStat label="Ojos de refractiva" value={res.refractiva.ojos} hint={res.refractiva.n ? `±0,50 D: ${dec(res.refractiva.pct_050, 0)} %` : ''} />
          <MiniStat label="Eficacia / seguridad" value={res.refractiva.n ? `${dec(res.refractiva.eficacia_media, 2)} / ${dec(res.refractiva.seguridad_media, 2)}` : '—'} hint={res.refractiva.n ? `AVsc ≥ 0,8: ${dec(res.refractiva.pct_av_sc_08, 0)} %` : ''} />
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mb-3">
        {TABLAS.map(([k, t]) => (
          <Button key={k} size="sm" variant={tabla === k ? 'default' : 'outline'} onClick={() => setTabla(k)} data-testid={`estudio-tabla-${k}`}>
            {t} <span className="ml-1 opacity-70">({res?.recuentos?.[k] ?? 0})</span>
          </Button>
        ))}
      </div>

      {FILTRO[tabla] && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{FILTRO[tabla][1]}:</span>
          <select value={filtro} onChange={(e) => setFiltro(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="estudio-filtro">
            <option value="">Todos</option>
            {opcionesFiltro.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      )}

      {tabla === 'lio' && rl && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-4">
          <Histograma errores={erroresLio} titulo={`Error de predicción (EE residual − esperada) · ${rl.n} ojos`} />
          <div className="cp-card p-4 text-sm space-y-1" data-testid="estudio-lio-resumen">
            <div className="font-display font-semibold mb-1">{filtro || 'Todas las lentes'}</div>
            <div>Error medio: <b>{dec(rl.media, 2)} D</b> · error absoluto medio: <b>{dec(rl.mae, 2)} D</b></div>
            <div>Dentro de ±0,50 D: <b>{dec(rl.p50, 0)} %</b> · dentro de ±1,00 D: <b>{dec(rl.p100, 0)} %</b></div>
            <div className="pt-2 text-xs text-muted-foreground">Por modelo (todas las fechas del filtro):</div>
            <table className="w-full text-xs">
              <thead><tr className="text-muted-foreground text-left"><th>Modelo</th><th>Ojos</th><th>Media</th><th>MAE</th><th>±0,50</th></tr></thead>
              <tbody>
                {Object.entries(res?.lio?.por_modelo || {}).map(([m, r]) => (
                  <tr key={m}><td>{m}</td><td>{r.n}</td><td>{dec(r.media, 2)}</td><td>{dec(r.mae, 2)}</td><td>{dec(r.pct_050, 0)} %</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tabla === 'refractiva' && rr && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <Histograma errores={erroresRef} titulo={`EE residual frente al objetivo · ${rr.n} ojos`} />
          <div className="cp-card p-4">
            <div className="font-display font-semibold text-sm mb-2">Predictibilidad: corrección intentada frente a conseguida (D)</div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <ScatterChart margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,140,135,0.25)" />
                  <XAxis type="number" dataKey="x" name="Intentada" domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                  <YAxis type="number" dataKey="y" name="Conseguida" domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${dec(v, 2)} D`} />
                  <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 14, y: 14 }]} stroke="#94A3B8" strokeDasharray="4 4" />
                  <Scatter data={dispersion} fill="#2E6E5E" isAnimationActive={false} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="cp-card p-0 overflow-hidden">
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-xs" data-testid="estudio-tabla">
            <thead className="sticky top-0 bg-[var(--cp-soft)] dark:bg-card">
              <tr>{cols.map(([k, t]) => <th key={k} className="text-left px-2 py-2 font-semibold whitespace-nowrap">{t}</th>)}</tr>
            </thead>
            <tbody>
              {filas.slice(0, 1000).map((r, i) => (
                <tr key={i} className="border-t">
                  {cols.map(([k]) => (
                    <td key={k} className={`px-2 py-1.5 whitespace-nowrap ${(k === 'error_prediccion' || k === 'error_vs_objetivo') && typeof r[k] === 'number' ? (Math.abs(r[k]) <= 0.5 ? 'text-emerald-700' : 'text-amber-700') : ''}`}>{celda(r[k])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!filas.length && <div className="p-8 text-center text-sm text-muted-foreground">Sin datos con estos filtros.</div>}
        </div>
        {filas.length > 1000 && <div className="p-2 text-xs text-muted-foreground">Se ven las 1.000 primeras de {filas.length}; el CSV y el Excel las llevan todas.</div>}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        EE = esfera + cilindro / 2. Error de predicción = EE postoperatorio − refracción esperada de la LIO (la última refracción
        postoperatoria). Eficacia = AVsc postoperatoria / AVcc previa; seguridad = AVcc postoperatoria / AVcc previa. El código de
        estudio es el mismo para un paciente en todas las tablas y exportaciones, y no permite saber quién es fuera de la clínica.
      </p>
    </div>
  );
}
